import { useState } from 'react';
import { Copy, BookOpen, X, Sun, Moon } from 'lucide-react';

interface LetterInspectorProps {
  bodyText: string;
  draftText: string | null;
  polishText: string | null;
  polishSkipped: boolean | null;
  letterView: 'final' | 'draft' | 'polish';
  onViewChange: (view: 'final' | 'draft' | 'polish') => void;
  onCopyText: (text: string, label: string) => void;
  subject?: string;
}

interface LetterStats {
  words: number;
  paragraphs: number;
  sentences: number;
}

function getLetterStats(text: string): LetterStats {
  const normalized = text.trim();
  if (!normalized) {
    return { words: 0, paragraphs: 0, sentences: 0 };
  }
  const words = normalized.split(/\s+/).filter(Boolean).length;
  const paragraphs = normalized.split(/\n\s*\n/).filter((chunk) => chunk.trim().length > 0).length;
  const sentences = normalized
    .split(/[.!?]+/g)
    .map((chunk) => chunk.trim())
    .filter(Boolean).length;
  return { words, paragraphs, sentences };
}

export function LetterInspector({
  bodyText,
  draftText,
  polishText,
  polishSkipped,
  letterView,
  onViewChange,
  onCopyText,
  subject,
}: LetterInspectorProps) {
  const [showFocusView, setShowFocusView] = useState(false);
  const [focusTheme, setFocusTheme] = useState<'light' | 'dark'>(() => {
    const saved = localStorage.getItem('letter-focus-theme');
    return saved === 'dark' ? 'dark' : 'light';
  });

  const toggleFocusTheme = () => {
    const newTheme = focusTheme === 'light' ? 'dark' : 'light';
    setFocusTheme(newTheme);
    localStorage.setItem('letter-focus-theme', newTheme);
  };

  const resolvedText =
    letterView === 'draft'
      ? draftText ?? bodyText
      : letterView === 'polish'
        ? polishText ?? bodyText
        : bodyText;

  const stats = getLetterStats(resolvedText);

  return (
    <>
      <div className="bg-night border border-stone/30 rounded-xl p-6 space-y-4">
        {/* Header with view toggle */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-lg text-parchment">Letter Inspector</h2>
            <div className="text-xs text-mist">
              {stats.words} words · {stats.paragraphs} paragraphs · {stats.sentences} sentences
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <button
              onClick={() => onViewChange('final')}
              className={`px-2 py-1 rounded border ${
                letterView === 'final'
                  ? 'border-rune text-parchment bg-rune/10'
                  : 'border-stone/30 text-mist hover:text-parchment'
              }`}
            >
              Final
            </button>
            <button
              onClick={() => onViewChange('draft')}
              disabled={!draftText}
              className={`px-2 py-1 rounded border ${
                letterView === 'draft'
                  ? 'border-rune text-parchment bg-rune/10'
                  : 'border-stone/30 text-mist hover:text-parchment'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              Draft
            </button>
            <button
              onClick={() => onViewChange('polish')}
              disabled={!polishText && !polishSkipped}
              className={`px-2 py-1 rounded border ${
                letterView === 'polish'
                  ? 'border-rune text-parchment bg-rune/10'
                  : 'border-stone/30 text-mist hover:text-parchment'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              Polish
            </button>
            <button
              onClick={() => setShowFocusView(true)}
              className="px-2 py-1 rounded border border-rune/50 text-rune hover:bg-rune/10 flex items-center gap-1"
              title="Open focus view"
            >
              <BookOpen size={12} />
              Focus
            </button>
          </div>
        </div>

        {/* Letter text */}
        <pre className="p-4 bg-void rounded-lg text-mist text-sm whitespace-pre-wrap overflow-auto max-h-[600px]">
          {resolvedText}
        </pre>

        {/* Copy buttons */}
        <div className="flex flex-wrap gap-2 text-xs text-mist">
          <button
            onClick={() => onCopyText(resolvedText, 'Visible text')}
            className="flex items-center gap-1 px-3 py-1 rounded border border-stone/30 hover:text-parchment"
          >
            <Copy size={12} />
            Copy Visible Text
          </button>
          {letterView !== 'final' && (
            <button
              onClick={() => onCopyText(bodyText, 'Final letter')}
              className="flex items-center gap-1 px-3 py-1 rounded border border-stone/30 hover:text-parchment"
            >
              <Copy size={12} />
              Copy Final Letter
            </button>
          )}
        </div>
      </div>

      {/* Focus View Modal */}
      {showFocusView && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setShowFocusView(false)}
        >
          <div
            className="relative w-full max-w-4xl max-h-[90vh] mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Controls */}
            <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
              {/* Theme toggle */}
              <button
                onClick={toggleFocusTheme}
                className={`p-2 rounded-full transition-colors ${
                  focusTheme === 'light'
                    ? 'bg-stone/30 text-mist hover:text-parchment hover:bg-stone/50'
                    : 'bg-parchment/20 text-parchment/70 hover:text-parchment hover:bg-parchment/30'
                }`}
                title={focusTheme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              >
                {focusTheme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
              </button>
              {/* Close button */}
              <button
                onClick={() => setShowFocusView(false)}
                className={`p-2 rounded-full transition-colors ${
                  focusTheme === 'light'
                    ? 'bg-stone/30 text-mist hover:text-parchment hover:bg-stone/50'
                    : 'bg-parchment/20 text-parchment/70 hover:text-parchment hover:bg-parchment/30'
                }`}
              >
                <X size={20} />
              </button>
            </div>

            {/* Letter page */}
            <div
              className={`rounded-lg shadow-2xl overflow-auto max-h-[90vh] transition-colors ${
                focusTheme === 'light'
                  ? 'bg-gradient-to-b from-amber-50 to-amber-100'
                  : 'bg-gradient-to-b from-slate-900 to-slate-950'
              }`}
            >
              {/* Paper texture overlay */}
              <div className="relative px-12 py-16 md:px-20 md:py-20">
                {/* Decorative header line */}
                <div
                  className={`absolute top-8 left-12 right-12 md:left-20 md:right-20 h-px bg-gradient-to-r from-transparent to-transparent ${
                    focusTheme === 'light' ? 'via-amber-400/50' : 'via-slate-600'
                  }`}
                />

                {/* Subject/Title */}
                {subject && (
                  <h1
                    className={`font-serif text-2xl md:text-3xl text-center mb-8 leading-relaxed ${
                      focusTheme === 'light' ? 'text-amber-900' : 'text-parchment'
                    }`}
                  >
                    {subject}
                  </h1>
                )}

                {/* Letter body */}
                <div
                  className={`font-serif text-base md:text-lg leading-relaxed whitespace-pre-wrap ${
                    focusTheme === 'light' ? 'text-amber-950' : 'text-slate-300'
                  }`}
                >
                  {resolvedText}
                </div>

                {/* Decorative footer line */}
                <div
                  className={`absolute bottom-8 left-12 right-12 md:left-20 md:right-20 h-px bg-gradient-to-r from-transparent to-transparent ${
                    focusTheme === 'light' ? 'via-amber-400/50' : 'via-slate-600'
                  }`}
                />
              </div>
            </div>

            {/* Word count badge */}
            <div className="absolute bottom-4 left-4 text-xs text-mist/70 bg-black/50 px-2 py-1 rounded">
              {stats.words} words
            </div>
          </div>
        </div>
      )}
    </>
  );
}
